import React, { createContext, useContext, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsApi } from '../services/api';
import { getLanguage, splitLanguagePath } from '../i18n/locales';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};

const applyHtmlAttrs = (lang) => {
  const meta = getLanguage(lang);
  if (typeof document === 'undefined' || !meta) return;
  document.documentElement.setAttribute('lang', meta.code);
  document.documentElement.setAttribute('dir', meta.dir);
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    applyHtmlAttrs(i18n.language);
    const handler = (lng) => applyHtmlAttrs(lng);
    i18n.on('languageChanged', handler);
    return () => i18n.off('languageChanged', handler);
  }, [i18n]);

  // For app routes (/app/*) we just swap translation in place and persist to backend.
  const setLanguage = useCallback(async (lang) => {
    await i18n.changeLanguage(lang);
    localStorage.setItem('bb_language', lang);
    try {
      // Dotted path - see ThemeContext: a nested object here would $set-overwrite
      // the whole ui_preferences subtree and wipe view catalogs/prefs.
      await settingsApi.update({ 'ui_preferences.language': lang });
    } catch {  // offline or signed out: the language is already on and kept on this device
    }
  }, [i18n]);

  // For public/marketing routes the URL itself encodes the language (/de/pricing).
  // Switching there means a navigation: rewrite the prefix and full-reload so the
  // BrowserRouter `basename` picks up the new value.
  const switchLanguageInUrl = useCallback((lang) => {
    if (typeof window === 'undefined') return;
    const { rest } = splitLanguagePath(window.location.pathname);
    const search = window.location.search || '';
    const hash = window.location.hash || '';
    localStorage.setItem('bb_language', lang);
    window.location.assign(`/${lang}${rest === '/' ? '' : rest}${search}${hash}`);
  }, []);

  const syncFromBackend = useCallback((backendLang) => {
    if (!backendLang) {
      // A new account has none yet: it keeps the language the person signed up in (the
      // site's or the browser's) instead of switching to English.
      const current = (i18n.resolvedLanguage || i18n.language || '').split('-')[0];
      if (current) settingsApi.update({ 'ui_preferences.language': current }).catch(() => {});
      return;
    }
    if (backendLang !== i18n.language) {
      i18n.changeLanguage(backendLang);
      localStorage.setItem('bb_language', backendLang);
    }
  }, [i18n]);

  return (
    <LanguageContext.Provider value={{
      language: i18n.language,
      setLanguage,
      switchLanguageInUrl,
      syncFromBackend,
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
